import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid, parseISO } from 'date-fns';

import GenerateGraphModal, { GenerateGraphValues } from '../GenerateGraphModal/GenerateGraphModal';
import { ReactComponent as GraphIcon } from './assets/graph-widget.svg';
import styles from './GraphWidget.module.scss';

// Universal graph-generation widget for case / topic / actor group / actor pages.
// Purely presentational: the host page owns all data fetching and passes state
// down. Generate and Regenerate both go through the Generate Graph modal (name +
// optional analysis window).

export type GraphWidgetStatus = 'idle' | 'generating' | 'ready' | 'failed';

export interface GraphWidgetStats {
  actors: number;
  relations: number;
  communities: number;
  bridgingActors: number;
}

interface GraphWidgetProps {
  // per-page texts (already translated by the host page) — the expected
  // generation time differs by source type (case ~20min, topic 3-20min,
  // actor group ~1h), so the host page supplies its own generatingHint
  title: string;
  description: string;
  generatingHint: string;
  status: GraphWidgetStatus;
  // ISO timestamp of the last successful generation — shown in the ready state
  generatedAt?: string | null;
  stats?: GraphWidgetStats | null;
  // disables the action buttons while a start/cancel request is in flight
  isBusy?: boolean;
  // whether the Generate Graph modal offers an analysis date range picker —
  // actor-kind graphs only (actor group / single actor sources). The ready-state
  // meta row shows the graph's period for every kind regardless.
  showDateRange: boolean;
  // name of the source entity (case / threat / topic / group / actor) —
  // prefilled as the default graph name in the generate modal
  entityName?: string | null;
  // current graph name + window, prefilled into the regenerate modal
  graphName?: string | null;
  graphStartDate?: Date | null;
  graphEndDate?: Date | null;
  onGenerate: (values: GenerateGraphValues) => void;
  onCancel: () => void;
  // called without values from the failed-state "Try again" (straight re-run
  // of the stored request, no modal)
  onRegenerate: (values?: GenerateGraphValues) => void;
  onOpen: () => void;
}

const UK_LANGUAGES = ['uk', 'ua', 'ua-UK', 'ru', 'ru-RU'];

// Backend timestamps are naive UTC (same convention as StoryCard) — append Z
// unless the string already carries timezone info.
function formatGeneratedAt(iso: string): string {
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const date = parseISO(hasTimezone ? iso : `${iso}Z`);
  if (!isValid(date)) return iso;
  // per the design: 17-07-2026, same in every language
  return format(date, 'dd-MM-yyyy');
}

// analysis-window date: 11/08/26, same in every language — one format with the
// graphs-list rows and the Generate Graph modal's datepicker (dd/MM/yy)
function formatWindowDate(date: Date | null): string {
  return date && isValid(date) ? format(date, 'dd/MM/yy') : '…';
}

export function GraphWidget({
  title,
  description,
  generatingHint,
  status,
  generatedAt = null,
  stats = null,
  isBusy = false,
  showDateRange,
  entityName = null,
  graphName = null,
  graphStartDate = null,
  graphEndDate = null,
  onGenerate,
  onCancel,
  onRegenerate,
  onOpen,
}: GraphWidgetProps) {
  const { t, i18n } = useTranslation();
  const [modalMode, setModalMode] = useState<'generate' | 'regenerate' | null>(null);

  const formatStat = (value: number) =>
    value.toLocaleString(UK_LANGUAGES.includes(i18n.language) ? 'uk-UA' : 'en-GB');

  const metrics: { label: string; value: number }[] = stats
    ? [
        { label: t('Actors'), value: stats.actors },
        { label: t('Relations'), value: stats.relations },
        { label: t('Communities'), value: stats.communities },
        { label: t('Bridging actors'), value: stats.bridgingActors },
      ]
    : [];

  return (
    <section className={styles.widget} aria-label={title}>
      <div className={styles.iconBox} aria-hidden="true">
        <GraphIcon />
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{title}</h3>

        {/* always rendered so screen readers announce Generating / Generation
            failed transitions; hidden via :empty in the other states */}
        <p
          role="status"
          className={`${styles.status} ${
            status === 'failed' ? styles.statusFailed : styles.statusGenerating
          }`}
        >
          {status === 'generating' && (
            <>
              <span className={styles.dot} aria-hidden="true" />
              {t('Generating')}
            </>
          )}
          {status === 'failed' && t('Generation failed')}
        </p>

        {status === 'idle' && <p className={styles.description}>{description}</p>}

        {status === 'generating' && <p className={styles.description}>{generatingHint}</p>}

        {status === 'ready' && (
          <p className={styles.metaRow}>
            {/* every graph kind carries a period now — actor graphs their
                analysis window, case/topic/threat graphs the case's own period
                (both null = the whole history) */}
            <span>
              {t('Date range')}{' '}
              {graphStartDate || graphEndDate
                ? `${formatWindowDate(graphStartDate)} - ${formatWindowDate(graphEndDate)}`
                : t('All time')}
            </span>
            {generatedAt && <span className={styles.metaDot} aria-hidden="true" />}
            {generatedAt && (
              <span>
                {t('Generated')} {formatGeneratedAt(generatedAt)}
              </span>
            )}
          </p>
        )}

        {status === 'ready' && metrics.length > 0 && (
          <ul className={styles.metrics}>
            {metrics.map((metric) => (
              <li key={metric.label} className={styles.metric}>
                <span className={styles.metricValue}>{formatStat(metric.value)}</span>
                <span className={styles.metricLabel}>{metric.label}</span>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.actions}>
          {status === 'idle' && (
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={isBusy}
              onClick={() => setModalMode('generate')}
            >
              {t('Generate graph')}
            </button>
          )}

          {status === 'generating' && (
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={isBusy}
              onClick={onCancel}
            >
              {t('Cancel generation')}
            </button>
          )}

          {status === 'ready' && (
            <>
              <button type="button" className={styles.primaryBtn} onClick={onOpen}>
                {t('Open graph')}
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={isBusy}
                onClick={() => setModalMode('regenerate')}
              >
                {t('Regenerate')}
              </button>
            </>
          )}

          {status === 'failed' && (
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={isBusy}
              onClick={() => onRegenerate()}
            >
              {t('Try again')}
            </button>
          )}
        </div>
      </div>

      {modalMode !== null && (
        <GenerateGraphModal
          mode={modalMode}
          showDateRange={showDateRange}
          initialName={modalMode === 'regenerate' ? graphName ?? '' : entityName ?? ''}
          initialStartDate={modalMode === 'regenerate' ? graphStartDate : null}
          initialEndDate={modalMode === 'regenerate' ? graphEndDate : null}
          onClose={() => setModalMode(null)}
          onSubmit={(values) => {
            const mode = modalMode;
            setModalMode(null);
            if (mode === 'generate') {
              onGenerate(values);
            } else {
              onRegenerate(values);
            }
          }}
        />
      )}
    </section>
  );
}
